import { useEffect } from "react";
import {
  BooleanInput,
  NumberInput,
  ReferenceArrayInput,
  ReferenceInput,
  SelectArrayInput,
  SelectInput,
  maxValue,
  minValue,
  required,
  useChoicesContext,
} from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";
import { studentRequests } from "api/studentApi";
import { CurriculumControlForms } from "utils/constants";
import { integerValidator } from "utils/validations";
import { useCurriculum, useMaxSemester } from "../CurriculumContext";
import { useCollectionActions } from "../collections/CollectionContext";
import { DisciplineInput, GridItem, HoursInput } from "./Partials";

// A module belongs to at most one discipline, so the ones already taken are dropped.
const ModulesArrayInput = ({ excludeIds }) => {
  const { allChoices } = useChoicesContext();
  const choices = (allChoices ?? []).filter(choice => !excludeIds.includes(choice.id));

  return (
    <SelectArrayInput
      source="moduleIds"
      optionText="name"
      label="Модули"
      choices={choices}
      fullWidth
    />
  );
};

const DisciplineFields = ({ rowIndex }) => {
  const curriculum = useCurriculum();
  const { items } = useCollectionActions();
  const { setValue } = useFormContext();

  const formOfControl = useWatch({ name: "formOfControl" });
  const isGradeAfterCycle = formOfControl === CurriculumControlForms.GradeAfterCycle;

  const maxSemester = useMaxSemester();

  const excludeModuleIds = items
    .filter((_, index) => index !== rowIndex)
    .flatMap(item => item?.moduleIds ?? [])
    .filter(id => id != null);

  useEffect(() => {
    if (isGradeAfterCycle) {
      setValue("finalExamInSemester", 0);
    }
  }, [setValue, isGradeAfterCycle]);

  return (
    <>
      <DisciplineInput />

      <GridItem md={6}>
        <ReferenceInput
          source="formOfControl"
          reference={studentRequests.getCurriculumsControlFormsSelect()}
          perPage={1000}
        >
          <SelectInput
            optionText="name"
            label="Форма на контрол"
            fullWidth
            validate={required("Моля, изберете форма на контрол.")}
          />
        </ReferenceInput>
      </GridItem>

      <GridItem md={3}>
        <NumberInput
          source="finalExamInSemester"
          label="Изпит в семестър"
          fullWidth
          readOnly={isGradeAfterCycle}
          min={0}
          max={maxSemester}
          step={1}
          helperText="0 при оценка след цикъл"
          validate={[
            required("Полето е задължително."),
            minValue(0, "Минималната стойност е 0."),
            maxValue(maxSemester, `Максималната стойност е ${maxSemester}.`),
            integerValidator,
          ]}
          onWheel={e => e.target.blur()}
        />
      </GridItem>

      <GridItem md={3}>
        <BooleanInput
          source="examDuringSemester"
          label="Изпит по време на семестъра"
        />
      </GridItem>

      <HoursInput
        source="lecturesAuditoriumOccupancy"
        label="Общ брой часове лекции"
        md={3}
        isRequired
      />
      <HoursInput
        source="seminarExercisesAuditoriumOccupancy"
        label="Общ брой часове СУ"
        md={3}
      />
      <HoursInput
        source="laboratoryExercisesAuditoriumOccupancy"
        label="Общ брой часове ЛУ"
        md={3}
      />
      <HoursInput
        source="practicalExercisesAuditoriumOccupancy"
        label="Общ брой часове ПУ"
        md={3}
      />
      <HoursInput
        source="extracurricularActivities"
        label="Извънаудиторна заетост"
        md={6}
        isRequired
      />

      <GridItem md={6}>
        <ReferenceArrayInput
          source="moduleIds"
          reference={studentRequests.getCurriculumModulesSelect(curriculum.id)}
        >
          <ModulesArrayInput excludeIds={excludeModuleIds} />
        </ReferenceArrayInput>
      </GridItem>
    </>
  );
};

export default DisciplineFields;
